import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { fetchMediaLibrary } from 'modules/mediaLibrary/actions';

import { ALL_MODE, LIBRARY_TYPES } from 'modules/mediaLibrary/constants';

import { getDefaultFetchParamsByMode, getSortedMediasByMode } from 'modules/mediaLibrary/selector';
import { getCurrentUserIsAdmin } from 'modules/users/selectors/currentUser';

import SearchInput from 'ui/fields/SearchInput';
import { ucFirst } from 'utils/global';

import { FromLibraryWrapper, LibraryHeader, SelectLibrary, LibraryWrapper } from './UI';
import Library from '../MediaLibrary/Library';

/**
 * @typedef {{
 * disabledAddMedia: boolean,
 * filters: import('modules/mediaLibrary/types').MediaLibraryFilters,
 * library: import('modules/mediaLibrary/constants').LIBRARIES_TYPES,
 * selectedMedias: import('modules/mediaLibrary/types').UploadModalMedia[],
 * onSelect: (media: import('modules/mediaLibrary/types').UploadModalMedia) => void,
 * onLibraryChange: (library: import('modules/mediaLibrary/constants').LIBRARIES_TYPES) => void,
 * }} FromLibraryProps
 */

/** @type {React.FC<FromLibraryProps>} */
const FromLibrary = ({ disabledAddMedia, onSelect, selectedMedias, library, onLibraryChange, filters = {} }) => {
  const dispatch = useDispatch();

  const isAdmin = useSelector(getCurrentUserIsAdmin);
  const medias = useSelector(state => getSortedMediasByMode(state, library));
  const params = useSelector(state => getDefaultFetchParamsByMode(state, library));

  const [search, setSearch] = React.useState('');

  const handleCheckMedia = mediaId => {
    const shouldUnselect = selectedMedias.some(({ id }) => mediaId === id);
    if (shouldUnselect) {
      onSelect(states => states.filter(media => media.id !== mediaId));
    } else if (!disabledAddMedia) {
      const media = medias.find(singleMedia => singleMedia.id === mediaId);
      onSelect(states => [...states, { ...media, isMediaLibraryItem: true }]);
    }
  };

  const fetchMedias = React.useCallback(
    pageParams => {
      dispatch(
        fetchMediaLibrary(
          {
            ...params,
            ...(search === '' ? filters : { ...filters, text: search }),
            ...pageParams,
          },
          { source: `dashboard-${library}` }
        )
      );
    },
    [dispatch, filters, params, search, library]
  );

  const fetchNextPage = () => fetchMedias({ page: params.page + 1 });

  const libraryTypeOptions = React.useMemo(
    () =>
      LIBRARY_TYPES.reduce((acc, type) => {
        const allowed = type !== ALL_MODE || !isAdmin;
        if (allowed) {
          acc.push({
            value: type,
            label: (
              <FormattedMessage
                id={`mediaLibrary.library_${type}`}
                defaultMessage={`${ucFirst(type.split('_')[0])} library`}
              />
            ),
          });
        }
        return acc;
      }, []),
    [isAdmin]
  );

  React.useEffect(() => fetchMedias({ number: 0, page: 0 }), [library, search]);

  return (
    <FromLibraryWrapper>
      <LibraryHeader>
        <FormattedMessage id="mediaLibrary.mediaList" defaultMessage="Media List" />
        <SelectLibrary value={library} onChange={onLibraryChange} options={libraryTypeOptions} />
        <SearchInput
          onSearch={setSearch}
          defaultValue={search}
          data-cy="mediaName-search"
        />
      </LibraryHeader>
      <LibraryWrapper>
        <Library
          shortCard
          showCheckBox
          mode={library}
          medias={medias}
          className="upload-modal"
          fetchNextPage={fetchNextPage}
          onMediaClick={handleCheckMedia}
          selectedMedias={selectedMedias}
          scrollProps={{ useParent: true }}
          disabledAddMedia={disabledAddMedia}
        />
      </LibraryWrapper>
    </FromLibraryWrapper>
  );
};

export default FromLibrary;
